import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell
} from 'recharts'
import LoadingProgress from '../components/LoadingProgress'
import ErrorMessage from '../components/ErrorMessage'
import { fetchOpsDashboard } from '../actions/opsdashboard'
import { currencySymbol, symbolFor } from '../utils/currency'
import {
  PRODUCT_LABELS, PAYMENT_LABELS, CASH_FULL_THRESHOLD, UNKNOWN, businessModelLabel, capToToday,
  buildContext, customerOf, customerName, filterRows, filterInstalledMachines, filterIncidences,
  aggregate, incidenceStats, incidencesOpenedBy, machineStatus, isMachineUp, groupByCustomer,
  installedBy, activeMachinesBy, gmvBy, sumMap, cumulativeTrend, isoDay, shiftYear, normalizePeriod, pctChange,
} from '../utils/opsDashboard'
import '../styles/DashboardView.css'
import '../styles/OperationalDashboardView.css'

// Operational dashboard for ADMIN MASTER — the VACWAYone counterpart of the
// lockers "Dashboard Operativo". Unlike /pro-dashboard it is computed from the
// sales themselves (GET /ops-dashboard), not from the Lambda arqueos: real
// charged amounts, all products, sale-time days and per-sale VAT.

const DONUT_COLORS = ['#0b97ff', '#06b6d4', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#f97316', '#ef4444', '#6366f1']

const STATUS_COLORS = {
  'ONLINE': '#28a745',
  'SLEEP ONLINE': '#28a745',
  'SLEEP': '#0066ff',
  'NIGHTSCAN': '#26c4f6',
  'MANTENIMIENTO': '#ff7b25',
  'SLEEP MAINTENANCE': '#ff7b25',
  'LOCKERS LLENOS': '#ff0000',
  'SIN TICKET': '#ff0000',
  'OFFLINE': '#727370',
  'UNKNOWN': '#727370',
}

const DONUT_SIZE = 200
const RADIAN = Math.PI / 180

// Percentage inside the outer ring; tiny slices stay unlabeled (legend has them).
function renderInsideLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (!percent || percent < 0.07) return null
  const r = innerRadius + (outerRadius - innerRadius) / 2
  const x = cx + r * Math.cos(-midAngle * RADIAN)
  const y = cy + r * Math.sin(-midAngle * RADIAN)
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700}>
      {`${Math.round(percent * 100)}%`}
    </text>
  )
}

const DONUT_METRICS = [
  { key: 'machines', label: 'Máquinas instaladas' },
  { key: 'gmv', label: 'GMV' },
]

const TOP_METRICS = [
  { key: 'gmv', label: 'GMV' },
  { key: 'transactions', label: 'Transacciones' },
  { key: 'commission', label: 'Comisión Vacway' },
  { key: 'incidences', label: 'Incidencias' },
]

function fmtNum(v, digits = 0) {
  if (v === null || v === undefined || isNaN(v)) return '—'
  return Number(v).toLocaleString('es-ES', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

function pct(v) {
  if (v === null || v === undefined || isNaN(v)) return '—'
  return (Number(v) * 100).toFixed(1) + '%'
}

function fmtDateTime(d) {
  if (!d) return '—'
  const date = new Date(d)
  if (isNaN(date)) return '—'
  return date.toLocaleString('es-ES', { hour12: false })
}

const CUSTOMER_FILTER_DELAY_MS = 300

// Current month to date: a full month would compare its empty future days
// against a complete month last year.
function defaultPeriod() {
  const today = new Date()
  const start = isoDay(new Date(today.getFullYear(), today.getMonth(), 1))
  return { start, end: isoDay(today), month: start.slice(0, 7) }
}

function periodFromInputs(s) {
  const value = s.periodType === 'month' ? s.tempMonth : s.periodType === 'year' ? s.tempYear : s.tempStart
  return normalizePeriod(s.periodType, value, s.tempEnd)
}

class OperationalDashboardView extends React.Component {
  constructor(props) {
    super(props)
    const p = defaultPeriod()
    this.state = {
      startDate: p.start,
      endDate: p.end,
      compareStartDate: shiftYear(p.start, -1),
      compareEndDate: shiftYear(p.end, -1),

      periodType: 'month',
      tempStart: p.start,
      tempEnd: p.end,
      tempMonth: p.month,
      tempYear: p.start.slice(0, 4),
      tempCompareStart: shiftYear(p.start, -1),
      tempCompareEnd: shiftYear(p.end, -1),
      periodError: null,

      customerText: '',      // what is typed
      customerFilter: '',    // applied (debounced) text filter
      customerId: '',        // exact customer picked from the list
      country: '',
      businessModel: '',
      currency: '',
      activeOnly: true,

      showAdvancedFilters: false,
      donutMetric: 'machines',
      topMetric: 'gmv',
      showAllAttention: false,

      data: null,
      cmpData: null,
      loading: false,
      error: null,
    }
  }

  componentDidMount() {
    if (this.canView()) this.load()
  }

  componentWillUnmount() {
    clearTimeout(this._customerTimer)
  }

  canView() {
    return this.props.role === 'ADMIN' && this.props.type === 'MASTER'
  }

  load = async () => {
    const { startDate, endDate, compareStartDate, compareEndDate } = this.state
    const hasCmp = !!(compareStartDate && compareEndDate)
    this.setState({ loading: true, error: null })

    const calls = [this.props.fetchOpsDashboard(startDate, endDate)]
    if (hasCmp) calls.push(this.props.fetchOpsDashboard(compareStartDate, compareEndDate))

    let cur, cmp
    try {
      [cur, cmp] = await Promise.all(calls)
    } catch (e) {
      this.setState({ loading: false, error: e.message || 'Error al cargar datos' })
      return
    }

    if (!cur || !cur.response) {
      const message = (cur && cur.error && cur.error.message) || 'Error al cargar datos'
      this.setState({ loading: false, error: message, data: null, cmpData: null })
      return
    }

    const data = cur.response
    const cmpData = hasCmp && cmp && cmp.response ? cmp.response : null

    // "Todas" by default; a picked currency is kept unless the new period lacks it.
    const currencies = this.currencyList(data)
    let currency = this.state.currency
    if (currency && !currencies.includes(currency)) currency = ''

    this.setState({ data, cmpData, currency, loading: false })
  }

  currencyList(data) {
    const set = new Set(((data && data.rows) || []).map(r => r.Currency).filter(Boolean))
    return Array.from(set).sort()
  }

  applyPeriod = () => {
    const s = this.state
    const raw = periodFromInputs(s)
    if (!raw) {
      this.setState({ periodError: 'Período no válido' })
      return
    }
    const period = capToToday(raw)
    if (!period) {
      this.setState({ periodError: 'El período empieza en el futuro' })
      return
    }
    const cmpEmpty = !s.tempCompareStart && !s.tempCompareEnd
    if (!cmpEmpty && (!s.tempCompareStart || !s.tempCompareEnd || s.tempCompareEnd < s.tempCompareStart)) {
      this.setState({ periodError: 'Período de comparación no válido' })
      return
    }
    this.setState({
      periodError: null,
      startDate: period.start,
      endDate: period.end,
      compareStartDate: cmpEmpty ? '' : s.tempCompareStart,
      compareEndDate: cmpEmpty ? '' : s.tempCompareEnd,
    }, this.load)
  }

  // Same dates one year earlier, up to today's day for a running period.
  fillLastYear = () => {
    const period = capToToday(periodFromInputs(this.state))
    if (!period) return
    this.setState({
      tempCompareStart: shiftYear(period.start, -1),
      tempCompareEnd: shiftYear(period.end, -1),
    })
  }

  // The input updates at once; the (costly) filtering waits until typing stops.
  // A name picked from the list filters by that exact customer, any other text
  // by customer name or machine serial.
  onCustomerText = (ctx, text) => {
    this.setState({ customerText: text })
    clearTimeout(this._customerTimer)
    this._customerTimer = setTimeout(() => {
      const q = text.trim().toLowerCase()
      const matches = Array.from(ctx.customersById.values()).filter(c => (c.Fullname || '').trim().toLowerCase() === q)
      this.setState({ customerFilter: text, customerId: matches.length === 1 ? matches[0]._id : '' })
    }, CUSTOMER_FILTER_DELAY_MS)
  }

  // ─── Formatting helpers bound to the selected currency ───────────────────

  symbol() {
    return this.state.currency ? symbolFor(this.state.currency) : currencySymbol()
  }

  money = (v) => {
    if (v === null || v === undefined || isNaN(v)) return '—'
    return `${fmtNum(v, 2)} ${this.symbol()}`
  }

  moneyShort = (v) => {
    if (v === null || v === undefined || isNaN(v)) return '—'
    return `${fmtNum(v, 0)} ${this.symbol()}`
  }

  // kind: 'currency' | 'count' | 'points' | 'hours'. invert: lower is better.
  renderDelta(cur, prev, kind, invert, hasCmp, cmpLabel) {
    if (!hasCmp || prev === null || prev === undefined || prev === 0 || cur === null || cur === undefined) {
      return <div className="small text-muted">{cmpLabel}: n/a</div>
    }
    const delta = cur - prev
    const rel = delta / Math.abs(prev)
    const good = invert ? delta < 0 : delta > 0
    const colorClass = delta === 0 ? 'text-muted' : good ? 'text-success' : 'text-danger'
    const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : ''
    const sign = delta >= 0 ? '+' : '-'
    let abs
    if (kind === 'points') abs = `${sign}${Math.abs(delta * 100).toFixed(2)} pp`
    else if (kind === 'count') abs = `${sign}${fmtNum(Math.abs(delta))}`
    else if (kind === 'hours') abs = `${sign}${fmtNum(Math.abs(delta), 1)} h`
    else abs = `${sign}${this.money(Math.abs(delta))}`
    return (
      <div className="small" style={{ marginTop: 2 }}>
        <span className="text-muted">{cmpLabel}:&nbsp;</span>
        <span className={colorClass}>{arrow} {(rel * 100).toFixed(1)}% ({abs})</span>
      </div>
    )
  }

  renderCard(cls, icon, label, value, delta, hint) {
    return (
      <div
        className={`dashboard-kpi-card ${cls}`}
        key={label}
        title={hint}
        style={{ minHeight: 96, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '10px 12px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className={`fas fa-${icon}`} style={{ fontSize: 12, color: '#6b7280' }} />
          <div className="kpi-label" style={{ margin: 0, fontSize: 12 }}>{label}</div>
        </div>
        <div style={{ marginTop: 8 }}>
          <div className="kpi-value" style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
        </div>
        <div style={{ marginTop: 8 }}>{delta}</div>
      </div>
    )
  }

  // Dual-ring donut: outer = current period, inner = comparison. Percentages
  // are drawn inside the outer ring (outside labels spilled over the legend),
  // and the legend sits against the right edge of the card.
  // formatShort: compact value for the centre, which must fit the inner ring.
  renderDonutCard(title, icon, current, compare, format, formatShort = format) {
    const total = current.reduce((s, d) => s + d.value, 0)
    const cmpTotal = (compare || []).reduce((s, d) => s + d.value, 0)
    const hasCmp = cmpTotal > 0
    const colorFor = (() => {
      const names = [...current.map(d => d.name), ...(compare || []).map(d => d.name)]
      const unique = Array.from(new Set(names))
      return name => DONUT_COLORS[unique.indexOf(name) % DONUT_COLORS.length]
    })()

    return (
      <div className="dashboard-card-section" key={title}>
        <div className="card-header-custom">
          <i className={`fas fa-${icon} mr-2`}></i>
          {title}
          {hasCmp && <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280', fontWeight: 400 }}>— comparación (interior)</span>}
        </div>
        <div className="card-body-custom">
          {current.length === 0 ? (
            <p className="text-muted text-center py-4">Sin datos</p>
          ) : (
            <div className="ops-donut">
              <div className="ops-donut-chart">
                <PieChart width={DONUT_SIZE} height={DONUT_SIZE}>
                  <Pie data={current} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={62} outerRadius={92} paddingAngle={2}
                    label={renderInsideLabel} labelLine={false} isAnimationActive={false}
                  >
                    {current.map(d => <Cell key={d.name} fill={colorFor(d.name)} />)}
                  </Pie>
                  {hasCmp && (
                    <Pie data={compare} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      innerRadius={44} outerRadius={57} paddingAngle={1} label={false} labelLine={false} isAnimationActive={false}
                    >
                      {compare.map(d => <Cell key={d.name} fill={colorFor(d.name)} fillOpacity={0.45} />)}
                    </Pie>
                  )}
                  <Tooltip formatter={(v, name) => [format(v), name]} />
                </PieChart>
                <div className={`ops-donut-center${hasCmp ? ' with-cmp' : ''}`}>
                  <div className="ops-donut-total">{formatShort(total)}</div>
                  {hasCmp && <div className="ops-donut-cmp">vs {formatShort(cmpTotal)}</div>}
                </div>
              </div>
              <ul className="ops-donut-legend">
                {current.map(d => {
                  const c = (compare || []).find(x => x.name === d.name)
                  return (
                    <li key={d.name}>
                      <div className="ops-donut-legend-text">
                        <div className="ops-donut-legend-name">{d.name}</div>
                        <div className="ops-donut-legend-value">
                          {format(d.value)} ({total > 0 ? ((d.value / total) * 100).toFixed(1) : 0}%)
                        </div>
                        {hasCmp && c && (
                          <div className="ops-donut-legend-value">
                            vs {format(c.value)} ({((c.value / cmpTotal) * 100).toFixed(1)}%)
                          </div>
                        )}
                      </div>
                      <span className="ops-donut-swatch" style={{ background: colorFor(d.name) }} />
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ─── Sections ─────────────────────────────────────────────────────────────

  renderHeader(ctx, options, periodTitle) {
    const { customerText, country, businessModel, currency, activeOnly, showAdvancedFilters, loading } = this.state
    return (
      <div className="dashboard-header">
        <div className="dashboard-title-section">
          <div>
            <h1 className="dashboard-title">
              <i className="fas fa-tachometer-alt"></i> Dashboard Operativo
            </h1>
            <div className="small text-muted mt-1">{periodTitle}</div>
          </div>
        </div>
        <div className="dashboard-actions">
          <input
            type="text"
            list="ops-customer-options"
            className="dashboard-filter-input"
            placeholder="Cliente o nº de serie..."
            value={customerText}
            onChange={e => this.onCustomerText(ctx, e.target.value)}
            style={{ width: 220 }}
          />
          <datalist id="ops-customer-options">
            {options.customers.map(n => <option key={n} value={n} />)}
          </datalist>
          <select className="dashboard-filter-input" value={country} onChange={e => this.setState({ country: e.target.value })} style={{ width: 150 }}>
            <option value="">País (todos)</option>
            {options.countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="dashboard-filter-input" value={businessModel} onChange={e => this.setState({ businessModel: e.target.value })} style={{ width: 170 }}>
            <option value="">Modelo negocio (todos)</option>
            {options.businessModels.map(b => <option key={b} value={b}>{businessModelLabel(b)}</option>)}
          </select>
          {options.currencies.length > 1 && (
            <select className="dashboard-filter-input" value={currency} onChange={e => this.setState({ currency: e.target.value })} style={{ width: 150 }} title="Las monedas no se convierten entre sí">
              <option value="">Todas (sin conversión)</option>
              {options.currencies.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {/* Own class names: the global .slider rule in style.css breaks .dashboard-switch */}
          <label className={`ops-switch${activeOnly ? ' on' : ''}`} title="Solo máquinas con ventas en el período">
            <input
              type="checkbox"
              role="switch"
              aria-checked={activeOnly}
              checked={activeOnly}
              onChange={e => this.setState({ activeOnly: e.target.checked })}
            />
            <span className="ops-switch-track" aria-hidden="true"></span>
            <span>Solo activas</span>
          </label>
          <button
            type="button"
            className="dashboard-filter-input toggle-filters"
            onClick={() => this.setState({ showAdvancedFilters: !showAdvancedFilters })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
          >
            <i className={`fas fa-chevron-${showAdvancedFilters ? 'up' : 'down'}`} />
            {showAdvancedFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={this.load} disabled={loading}>
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync'} mr-1`} />
            Actualizar
          </button>
        </div>
      </div>
    )
  }

  renderAdvancedFilters() {
    const s = this.state
    const input = (type, value, key, width = 150) => (
      <input type={type} className="dashboard-filter-input" value={value} onChange={e => this.setState({ [key]: e.target.value })} style={{ width }} />
    )
    return (
      <div className="dashboard-header mb-3" style={{ padding: '12px 20px', justifyContent: 'center' }}>
        <div className="d-flex align-items-center flex-wrap justify-content-center" style={{ gap: 8 }}>
          <span className="small text-muted">Período:</span>
          <select className="dashboard-filter-input" value={s.periodType} onChange={e => this.setState({ periodType: e.target.value })} style={{ width: 110 }}>
            <option value="range">Rango</option>
            <option value="day">Día</option>
            <option value="month">Mes</option>
            <option value="year">Año</option>
          </select>
          {s.periodType === 'range' && <>{input('date', s.tempStart, 'tempStart')}<span className="small text-muted">→</span>{input('date', s.tempEnd, 'tempEnd')}</>}
          {s.periodType === 'day' && input('date', s.tempStart, 'tempStart')}
          {s.periodType === 'month' && input('month', s.tempMonth, 'tempMonth')}
          {s.periodType === 'year' && input('number', s.tempYear, 'tempYear', 100)}

          <span className="small text-muted" style={{ marginLeft: 12 }}>
            Comparar con <em style={{ fontSize: 11 }}>(vacío = sin comparación)</em>
          </span>
          <button className="btn btn-sm btn-secondary" style={{ height: 36 }} onClick={this.fillLastYear} title="Mismo período del año anterior">
            Año ant.
          </button>
          {input('date', s.tempCompareStart, 'tempCompareStart')}
          <span className="small text-muted">→</span>
          {input('date', s.tempCompareEnd, 'tempCompareEnd')}
          <button className="btn btn-primary btn-sm" style={{ height: 36 }} onClick={this.applyPeriod} disabled={s.loading}>
            Cargar
          </button>
          {s.periodError && <span className="small text-danger">{s.periodError}</span>}
        </div>
      </div>
    )
  }

  renderTrend(trend, hasCmp) {
    const sym = this.symbol()
    return (
      <div className="dashboard-card-section">
        <div className="card-header-custom">
          <i className="fas fa-chart-line mr-2"></i>
          Tendencia acumulada GMV vs comisión Vacway
          {hasCmp && <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280', fontWeight: 400 }}>— comparación punteada, alineada por día del período</span>}
        </div>
        <div className="card-body-custom">
          {trend.length === 0 ? (
            <p className="text-muted text-center py-4">Sin datos</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trend} margin={{ top: 4, right: 20, bottom: 4, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="idx" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v, name) => [this.money(v), name]}
                  labelFormatter={(idx, payload) => {
                    const p = payload && payload[0] && payload[0].payload
                    if (!p) return `Día ${idx}`
                    return `Día ${idx}: ${p.day || '—'}${p.cmpDay ? ` (vs ${p.cmpDay})` : ''}`
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="gmv" stroke="#2563EB" strokeWidth={2} dot={false} name={`GMV neto (${sym})`} />
                <Line type="monotone" dataKey="commission" stroke="#059669" strokeWidth={2} dot={false} name={`Comisión Vacway (${sym})`} />
                {hasCmp && <Line type="monotone" dataKey="cmpGmv" stroke="#93c5fd" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="GMV comp." />}
                {hasCmp && <Line type="monotone" dataKey="cmpCommission" stroke="#6ee7b7" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Comisión comp." />}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    )
  }

  renderSegmented(options, value, onChange) {
    return (
      <div className="ops-segmented" role="group">
        {options.map(o => (
          <button key={o.key} type="button" className={value === o.key ? 'active' : ''} aria-pressed={value === o.key} onClick={() => onChange(o.key)}>
            {o.label}
          </button>
        ))}
      </div>
    )
  }

  renderTopCustomers(topRows, hasCmp) {
    const { topMetric } = this.state
    return (
      <div className="dashboard-card-section">
        <div className="card-header-custom">
          <i className="fas fa-trophy mr-2"></i>
          Top 5 clientes
          {this.renderSegmented(TOP_METRICS, topMetric, key => this.setState({ topMetric: key }))}
        </div>
        <div className="card-body-custom ops-table-wrap">
          {topRows.length === 0 ? (
            <p className="text-muted text-center py-4">Sin datos</p>
          ) : (
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Cliente</th>
                  <th>País</th>
                  <th className="ops-num">GMV</th>
                  <th className="ops-num">Comisión Vacway</th>
                  <th className="ops-num">Tx</th>
                  <th className="ops-num">Ticket medio</th>
                  <th className="ops-num" title="Con ventas / instaladas">Máquinas</th>
                  <th className="ops-num">Incidencias</th>
                </tr>
              </thead>
              <tbody>
                {topRows.map((r, i) => (
                  <tr key={r.key}>
                    <td className="ops-muted">{i + 1}</td>
                    <td className="ops-name">{r.name}</td>
                    <td>{r.country}</td>
                    <td className="ops-num">{this.money(r.agg.gmv)}</td>
                    <td className="ops-num">{r.isSale ? '—' : this.money(r.agg.commission)}</td>
                    <td className="ops-num">{fmtNum(r.agg.transactions)}</td>
                    <td className="ops-num">{this.money(r.agg.ticketAvg)}</td>
                    <td className="ops-num">{fmtNum(r.agg.activeMachines)} / {fmtNum(r.installed)}</td>
                    <td className="ops-num">{fmtNum(r.incidences)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    )
  }

  renderCustomerComparison(cmpRowsByCustomer, hasCmp) {
    const diff = (cur, prev) => {
      const p = pctChange(cur, prev)
      if (!hasCmp || p === null) return <td className="ops-num ops-muted">—</td>
      return <td className={`ops-num ${p > 0 ? 'text-success' : p < 0 ? 'text-danger' : 'ops-muted'}`}>{(p * 100).toFixed(1)}%</td>
    }
    return (
      <div className="dashboard-card-section">
        <div className="card-header-custom">
          <i className="fas fa-balance-scale mr-2"></i>
          Comparativa por cliente (período actual vs comparación)
        </div>
        <div className="card-body-custom ops-table-scroll" style={{ padding: 0 }}>
          {cmpRowsByCustomer.length === 0 ? (
            <p className="text-muted text-center py-4">Sin datos</p>
          ) : (
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Modelo</th>
                  <th className="ops-num">GMV actual</th>
                  <th className="ops-num">GMV comp.</th>
                  <th className="ops-num">% dif.</th>
                  <th className="ops-num">Tx actual</th>
                  <th className="ops-num">Tx comp.</th>
                  <th className="ops-num">% dif.</th>
                  <th className="ops-num">Com. Vacway actual</th>
                  <th className="ops-num">Com. comp.</th>
                  <th className="ops-num">% dif.</th>
                </tr>
              </thead>
              <tbody>
                {cmpRowsByCustomer.map(r => (
                  <tr key={r.key}>
                    <td className="ops-name">{r.name}</td>
                    <td className="small">{businessModelLabel(r.businessModel)}</td>
                    <td className="ops-num">{this.money(r.cur.gmv)}</td>
                    <td className="ops-num">{hasCmp ? this.money(r.cmp.gmv) : '—'}</td>
                    {diff(r.cur.gmv, r.cmp.gmv)}
                    <td className="ops-num">{fmtNum(r.cur.transactions)}</td>
                    <td className="ops-num">{hasCmp ? fmtNum(r.cmp.transactions) : '—'}</td>
                    {diff(r.cur.transactions, r.cmp.transactions)}
                    <td className="ops-num">{r.isSale ? '—' : this.money(r.cur.commission)}</td>
                    <td className="ops-num">{r.isSale || !hasCmp ? '—' : this.money(r.cmp.commission)}</td>
                    {r.isSale ? <td className="ops-num ops-muted">—</td> : diff(r.cur.commission, r.cmp.commission)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    )
  }

  renderFleet(fleet) {
    const { showAllAttention } = this.state
    const shown = showAllAttention ? fleet.attention : fleet.attention.slice(0, 15)
    return (
      <div className="dashboard-card-section">
        <div className="card-header-custom">
          <i className="fas fa-server mr-2"></i>
          Estado del parque
          <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280', fontWeight: 400 }}>
            — situación actual de las máquinas instaladas{this.state.activeOnly ? ' con ventas en el período' : ''}
          </span>
        </div>
        <div className="card-body-custom">
          <div className="ops-chips">
            {fleet.statusCounts.map(s => (
              <span key={s.name} className="ops-chip">
                <span className="ops-dot" style={{ background: STATUS_COLORS[s.name] || '#727370' }} />
                {s.name}: <strong>{s.value}</strong>
              </span>
            ))}
            <span className="ops-chip warn">
              <i className="fas fa-money-bill-wave" /> Billetero &gt; {fmtNum(CASH_FULL_THRESHOLD)}: <strong>{fleet.cashFull}</strong>
            </span>
            <span className="ops-chip danger">
              <i className="fas fa-tools" /> Incidencias abiertas: <strong>{fleet.openIncidences}</strong>
            </span>
          </div>
          {fleet.attention.length === 0 ? (
            <p className="text-muted text-center py-2">Ninguna máquina requiere atención</p>
          ) : (
            <div className="ops-table-wrap">
              <div className="small text-muted mb-2">Máquinas que requieren atención ({fleet.attention.length})</div>
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>Máquina</th>
                    <th>Cliente</th>
                    <th>Estado</th>
                    <th>Última señal</th>
                    <th className="ops-num">Tx período</th>
                    <th className="ops-num">Incid. abiertas</th>
                    <th className="ops-num">Billetero</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(m => (
                    <tr key={m.id}>
                      <td className="ops-name">{m.serial}</td>
                      <td>{m.customer}</td>
                      <td style={{ whiteSpace: 'nowrap' }}><span style={{ color: STATUS_COLORS[m.status] || '#727370', fontWeight: 600 }}>{m.status}</span></td>
                      <td className="small" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(m.lastSignal)}</td>
                      <td className="ops-num">{fmtNum(m.transactions)}</td>
                      <td className="ops-num">{fmtNum(m.openIncidences)}</td>
                      <td className="ops-num">{fmtNum(m.bills)}</td>
                      <td className="small">{m.reasons.join(' · ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {fleet.attention.length > 15 && (
                <button type="button" className="btn btn-link btn-sm" onClick={() => this.setState({ showAllAttention: !showAllAttention })}>
                  {showAllAttention ? 'Ver menos' : `Ver todas (${fleet.attention.length})`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ─── Derived data ─────────────────────────────────────────────────────────

  buildOptions(ctx, data) {
    const used = new Set()
    ;(data.rows || []).forEach(r => r.CustomerId && used.add(r.CustomerId))
    ;(data.machines || []).forEach(m => m.CustomerId && used.add(m.CustomerId))
    const customers = Array.from(used).map(id => customerOf(ctx, id)).filter(Boolean)
    const uniq = arr => Array.from(new Set(arr.filter(Boolean))).sort((a, b) => a.localeCompare(b))
    return {
      customers: uniq(customers.map(c => c.Fullname)),
      countries: uniq(customers.map(c => c.Country || UNKNOWN)),
      businessModels: uniq(customers.map(c => c.BusinessModel || UNKNOWN)),
      currencies: this.currencyList(data),
    }
  }

  buildFleet(ctx, installed, rows, incidences, activeOnly) {
    const now = Date.now()
    const txByMachine = {}
    rows.forEach(r => { if (r.MachineId) txByMachine[r.MachineId] = (txByMachine[r.MachineId] || 0) + r.Tickets })
    const openByMachine = {}
    incidences.forEach(i => {
      if (i.Status !== 'RESOLVED' && i.MachineId) openByMachine[i.MachineId] = (openByMachine[i.MachineId] || 0) + 1
    })

    const installedIds = new Set(installed.map(m => m._id))
    const statusCounts = {}
    let cashFull = 0
    const attention = []
    installed.forEach(m => {
      const status = machineStatus(m, now)
      statusCounts[status] = (statusCounts[status] || 0) + 1
      const bills = m.BillsInBox || 0
      if (bills > CASH_FULL_THRESHOLD) cashFull += 1

      const reasons = []
      if (status === 'OFFLINE' || status === 'UNKNOWN') reasons.push('Sin conexión')
      else if (!isMachineUp(status)) reasons.push(status)
      if (!activeOnly && !txByMachine[m._id]) reasons.push('Sin ventas en el período')
      if (openByMachine[m._id]) reasons.push('Incidencia abierta')
      if (bills > CASH_FULL_THRESHOLD) reasons.push('Billetero lleno')
      if (reasons.length === 0) return

      attention.push({
        id: m._id,
        serial: m.Serial || '—',
        customer: customerName(ctx, m.CustomerId),
        status,
        lastSignal: m.Last_signal,
        transactions: txByMachine[m._id] || 0,
        openIncidences: openByMachine[m._id] || 0,
        bills,
        reasons,
      })
    })
    attention.sort((a, b) => b.reasons.length - a.reasons.length || a.transactions - b.transactions)

    return {
      statusCounts: Object.entries(statusCounts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value),
      cashFull,
      openIncidences: incidences.filter(i => i.Status !== 'RESOLVED' && (!activeOnly || installedIds.has(i.MachineId))).length,
      attention,
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  render() {
    if (!this.canView()) return <ErrorMessage message="Acceso restringido a Super Admin" />

    const s = this.state
    const { data, cmpData, loading, error } = s

    const ctx = buildContext(data)
    const options = data ? this.buildOptions(ctx, data) : { customers: [], countries: [], businessModels: [], currencies: [] }
    const hasCmpRange = !!(s.compareStartDate && s.compareEndDate)
    const isLy = hasCmpRange && s.compareStartDate === shiftYear(s.startDate, -1) && s.compareEndDate === shiftYear(s.endDate, -1)
    const cmpLabel = isLy ? 'vs LY' : 'vs comp'
    const periodTitle = `Período: ${s.startDate} → ${s.endDate}` +
      (hasCmpRange ? ` (vs ${s.compareStartDate} → ${s.compareEndDate}${cmpData ? '' : ', sin datos'})` : ' (sin comparación)')

    const header = this.renderHeader(ctx, options, periodTitle)
    const advanced = s.showAdvancedFilters ? this.renderAdvancedFilters() : null

    if (error) {
      return <div className="dashboard-view">{header}{advanced}<ErrorMessage message={error} /></div>
    }
    if (loading || !data) {
      return <div className="dashboard-view">{header}{advanced}<LoadingProgress /></div>
    }

    const filters = {
      customerId: s.customerId, text: s.customerId ? '' : s.customerFilter,
      country: s.country, businessModel: s.businessModel, currency: s.currency,
    }
    const range = { start: s.startDate, end: s.endDate }
    const cmpRange = hasCmpRange ? { start: s.compareStartDate, end: s.compareEndDate } : null

    const rows = filterRows(data.rows, ctx, filters)
    const cmpRows = cmpData ? filterRows(cmpData.rows, ctx, filters) : []
    const hasCmp = cmpRows.length > 0

    const cur = aggregate(rows, ctx)
    const cmp = aggregate(cmpRows, ctx)

    const allInstalled = filterInstalledMachines(data.machines, ctx, filters)
    const installed = s.activeOnly ? allInstalled.filter(m => cur.activeMachineIds.has(m._id)) : allInstalled

    const incidences = filterIncidences(data.incidences, ctx, filters)
    const cmpIncidences = cmpData ? filterIncidences(cmpData.incidences, ctx, filters) : []
    const inc = incidenceStats(incidences, range.start, range.end)
    const cmpInc = cmpRange ? incidenceStats(cmpIncidences, cmpRange.start, cmpRange.end) : null
    const hasCmpInc = !!cmpData

    const delta = (c, p, kind, invert, enabled = hasCmp) => this.renderDelta(c, p, kind, invert, enabled, cmpLabel)

    // Donuts by customer attribute: installed machines (snapshot) or GMV.
    const byMachines = s.donutMetric === 'machines'
    const donutFormat = byMachines ? v => fmtNum(v) : v => this.money(v)
    const donutShort = byMachines ? v => fmtNum(v) : this.moneyShort
    const labelSlices = (key, slices) => key === 'BusinessModel' && slices
      ? slices.map(d => ({ ...d, name: d.name === 'Otros' ? d.name : businessModelLabel(d.name) }))
      : slices
    const donut = (key, title, icon) => byMachines
      ? this.renderDonutCard(title, icon, labelSlices(key, s.activeOnly ? activeMachinesBy(rows, ctx, key) : installedBy(installed, ctx, key)), null, donutFormat, donutShort)
      : this.renderDonutCard(title, icon, labelSlices(key, gmvBy(rows, ctx, key)), hasCmp ? labelSlices(key, gmvBy(cmpRows, ctx, key)) : null, donutFormat, donutShort)

    // Per-customer tables
    const curGroups = groupByCustomer(rows, ctx)
    const cmpGroups = groupByCustomer(cmpRows, ctx)
    const installedByCustomer = {}
    allInstalled.forEach(m => { installedByCustomer[m.CustomerId] = (installedByCustomer[m.CustomerId] || 0) + 1 })
    const incByCustomer = incidencesOpenedBy(incidences, range.start, range.end, i => i.CustomerId || 'none')

    const customerKeys = new Set([...curGroups.keys(), ...Object.keys(incByCustomer)])
    const topRows = Array.from(customerKeys).map(key => {
      const g = curGroups.get(key)
      const id = key === 'none' ? null : key
      const c = customerOf(ctx, id)
      const isSale = c ? String(c.BusinessModel || '').toUpperCase() === 'SALE' : false
      const agg = aggregate(g ? g.rows : [], ctx)
      return {
        key, name: customerName(ctx, id), country: (c && c.Country) || UNKNOWN, isSale, agg,
        installed: installedByCustomer[id] || 0,
        incidences: incByCustomer[key] || 0,
      }
    })
    const sortValue = r => s.topMetric === 'incidences' ? r.incidences
      : s.topMetric === 'transactions' ? r.agg.transactions
      : s.topMetric === 'commission' ? (r.isSale ? 0 : r.agg.commission)
      : r.agg.gmv
    const top5 = topRows.filter(r => sortValue(r) > 0).sort((a, b) => sortValue(b) - sortValue(a)).slice(0, 5)

    const compareKeys = new Set([...curGroups.keys(), ...cmpGroups.keys()])
    const customerComparison = Array.from(compareKeys).map(key => {
      const id = key === 'none' ? null : key
      const c = customerOf(ctx, id)
      return {
        key,
        name: customerName(ctx, id),
        businessModel: (c && c.BusinessModel) || UNKNOWN,
        isSale: c ? String(c.BusinessModel || '').toUpperCase() === 'SALE' : false,
        cur: aggregate(curGroups.has(key) ? curGroups.get(key).rows : [], ctx),
        cmp: aggregate(cmpGroups.has(key) ? cmpGroups.get(key).rows : [], ctx),
      }
    }).sort((a, b) => b.cur.gmv - a.cur.gmv || b.cmp.gmv - a.cmp.gmv)

    const trend = cumulativeTrend(rows, cmpRows, ctx, range, hasCmp ? cmpRange : null)
    const fleet = this.buildFleet(ctx, installed, rows, incidences, s.activeOnly)

    return (
      <div className="dashboard-view">
        {header}
        {advanced}

        {/* ── KPIs ── */}
        <div className="dashboard-kpi-row">
          {this.renderCard('total', 'coins', 'GMV neto', this.money(cur.gmv), delta(cur.gmv, cmp.gmv, 'currency'),
            'Igual que "Total Ventas Sin IVA" en Ventas: líneas de venta sin IVA, por fecha de registro')}
          {this.renderCard('green-block', 'hand-holding-usd', 'Comisión Vacway', this.money(cur.commission), delta(cur.commission, cmp.commission, 'currency'),
            'Neto × (1 − comisión cliente) por producto; excluye clientes en modelo Venta')}
          {this.renderCard('take-rate', 'percent', 'Take rate', pct(cur.takeRate), delta(cur.takeRate, cmp.takeRate, 'points'),
            'Comisión Vacway / GMV neto, sin clientes en modelo Venta')}
          {this.renderCard('pinpad-block', 'ticket-alt', 'Transacciones', fmtNum(cur.transactions), delta(cur.transactions, cmp.transactions, 'count'),
            'Ventas con importe > 0 (sin expediciones manuales ni devoluciones)')}
          {this.renderCard('pinpad-active', 'receipt', 'Ticket medio', this.money(cur.ticketAvg), delta(cur.ticketAvg, cmp.ticketAvg, 'currency'),
            'GMV neto / transacciones')}
          {this.renderCard('door-block', 'store', 'Máquinas activas', `${fmtNum(cur.activeMachines)} / ${fmtNum(allInstalled.length)}`,
            delta(cur.activeMachines, cmp.activeMachines, 'count'),
            'Máquinas con ventas en el período / máquinas instaladas hoy')}
          {this.renderCard('red-block', 'tools', 'Incidencias', fmtNum(inc.opened), delta(inc.opened, cmpInc && cmpInc.opened, 'count', true, hasCmpInc),
            'Incidencias abiertas durante el período')}
          {this.renderCard('broken', 'stopwatch', 'MTTR', inc.mttrHours === null ? '—' : `${fmtNum(inc.mttrHours, 1)} h`,
            delta(inc.mttrHours, cmpInc && cmpInc.mttrHours, 'hours', true, hasCmpInc),
            'Tiempo medio de resolución de las incidencias resueltas en el período')}
        </div>

        {/* ── Installed base / GMV share ── */}
        <div className="ops-section-bar">
          <h2>
            <i className="fas fa-chart-pie" />
            {byMachines ? 'Parque instalado' : 'Reparto del GMV'}
            <span className="ops-section-hint">
              {byMachines
                ? (s.activeOnly ? '— máquinas con ventas en el período, por el cliente de esas ventas' : '— máquinas asignadas hoy a un cliente')
                : '— período actual (exterior) vs comparación (interior)'}
            </span>
          </h2>
          {this.renderSegmented(DONUT_METRICS, s.donutMetric, key => this.setState({ donutMetric: key }))}
        </div>
        <div className="dashboard-graphs-grid">
          {donut('Country', byMachines ? 'Instaladas por país' : 'GMV por país', 'globe')}
          {donut('BusinessModel', byMachines ? 'Instaladas por modelo de negocio' : 'GMV por modelo de negocio', 'briefcase')}
        </div>

        {/* ── Per machine KPIs + trend ── */}
        <div className="dashboard-kpi-row">
          {this.renderCard('gmv', 'cash-register', 'GMV por máquina activa', this.money(cur.gmvPerActiveMachine),
            delta(cur.gmvPerActiveMachine, cmp.gmvPerActiveMachine, 'currency'), 'GMV neto / máquinas con ventas')}
          {this.renderCard('avg-ticket', 'calendar-day', 'GMV por máquina y día', this.money(cur.gmvPerMachineDay),
            delta(cur.gmvPerMachineDay, cmp.gmvPerMachineDay, 'currency'), 'GMV neto / días-máquina con ventas')}
          {this.renderCard('commission', 'hand-holding-usd', 'Comisión por máquina activa', this.money(cur.commissionPerActiveMachine),
            delta(cur.commissionPerActiveMachine, cmp.commissionPerActiveMachine, 'currency'), 'Comisión Vacway / máquinas con ventas')}
          {this.renderCard('machines', 'undo', 'Devoluciones', `${fmtNum(cur.refunds)} · ${this.money(Math.abs(cur.refundsNet))}`,
            delta(cur.refunds, cmp.refunds, 'count', true), 'Ventas con importe negativo (devoluciones de taquilla)')}
          {this.renderCard('transactions', 'hand-paper', 'Expediciones manuales', fmtNum(cur.manual),
            delta(cur.manual, cmp.manual, 'count', true), 'Ventas con importe 0 (TOKEN / mantenimiento)')}
        </div>
        {this.renderTrend(trend, hasCmp)}

        {/* ── Product & payment mix ── */}
        <div className="dashboard-graphs-grid">
          {this.renderDonutCard('Unidades por producto', 'box', sumMap(rows, 'Units', PRODUCT_LABELS),
            hasCmp ? sumMap(cmpRows, 'Units', PRODUCT_LABELS) : null, v => fmtNum(v))}
          {this.renderDonutCard('GMV neto por método de pago', 'credit-card', sumMap(rows, 'ByMethod', PAYMENT_LABELS),
            hasCmp ? sumMap(cmpRows, 'ByMethod', PAYMENT_LABELS) : null, v => this.money(v), this.moneyShort)}
        </div>

        {this.renderTopCustomers(top5, hasCmp)}
        {this.renderCustomerComparison(customerComparison, hasCmp)}
        {this.renderFleet(fleet)}

        <div className="ops-footnote">
          Datos calculados desde las ventas con los mismos criterios que la página Ventas ({fmtNum(data.salesCount)} ventas por fecha de registro en el período).
          Instaladas, estado y billetero son la situación actual, no la del período.
          {options.currencies.length > 1 && !s.currency && ' Atención: se están sumando importes de varias monedas sin conversión.'}
        </div>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  role: state.auth.role,
  type: state.auth.type,
})

const mapDispatchToProps = dispatch => ({
  fetchOpsDashboard: bindActionCreators(fetchOpsDashboard, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(OperationalDashboardView)
